import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, Crown, Zap, DollarSign, Ticket, 
  RefreshCw, ArrowLeft, Shield, TrendingUp,
  Copy, Check, CreditCard, Eye, EyeOff, Save, Trash2, AlertCircle, CheckCircle, Lock, Mail, BarChart3, Download, Package, Plus, Edit2, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  
  // Admin login state (completely independent from user auth)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminAccountPassword, setAdminAccountPassword] = useState('');
  const [adminPanelPassword, setAdminPanelPassword] = useState('');
  const [adminToken, setAdminToken] = useState('');
  const [verifyingLogin, setVerifyingLogin] = useState(false);
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  const [showPanelPassword, setShowPanelPassword] = useState(false);
  const [loginStep, setLoginStep] = useState(1); // 1 = email/password, 2 = admin password
  
  // Admin data state
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Stripe settings state
  const [stripeSettings, setStripeSettings] = useState(null);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  
  // GA4 settings state
  const [ga4Settings, setGa4Settings] = useState(null);
  const [ga4MeasurementId, setGa4MeasurementId] = useState('');
  const [savingGa4, setSavingGa4] = useState(false);

  // AI Features state
  const [aiFeatures, setAiFeatures] = useState({ post_editing_enabled: false, ai_optimization_enabled: false });
  const [savingAiFeatures, setSavingAiFeatures] = useState(false);

  // Plans state
  const [plans, setPlans] = useState({ monthly: [], lifetime: [] });
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedPlanForCoupons, setSelectedPlanForCoupons] = useState(null);
  const [couponCount, setCouponCount] = useState(100);
  const [generatingPlanCoupons, setGeneratingPlanCoupons] = useState(false);
  const [planFormData, setPlanFormData] = useState({
    name: '',
    plan_type: 'monthly',
    price: 0,
    stripe_price_id: '',
    display_order: 0,
    is_active: true,
    features: {
      max_sites: 1,
      max_posts_analyzed: 300,
      max_seats: 1,
      bulk_delete_limit: 50,
      has_ga4_connection: true,
      has_sorting_filtering: true,
      dashboard_days: -1
    }
  });

  // Check if admin session exists
  useEffect(() => {
    const storedToken = sessionStorage.getItem('adminToken');
    const storedPanelPassword = sessionStorage.getItem('adminPanelPassword');
    if (storedToken && storedPanelPassword) {
      setAdminToken(storedToken);
      setAdminPanelPassword(storedPanelPassword);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && adminToken) {
      fetchAdminData();
    }
  }, [adminToken, isAuthenticated]);

  // Step 1: Login with admin email + account password
  const handleAdminLogin = async () => {
    if (!adminEmail.trim() || !adminAccountPassword.trim()) {
      toast.error('Please enter email and password');
      return;
    }
    
    setVerifyingLogin(true);
    try {
      // Login to get JWT token
      const response = await axios.post(`${API}/auth/login`, {
        email: adminEmail,
        password: adminAccountPassword
      });
      
      const token = response.data.access_token;
      setAdminToken(token);
      setLoginStep(2); // Move to admin password step
      toast.success('Account verified. Enter admin password.');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else {
        toast.error('Login failed');
      }
    } finally {
      setVerifyingLogin(false);
    }
  };

  // Step 2: Verify admin panel password
  const verifyAdminPanelPassword = async () => {
    if (!adminPanelPassword.trim()) {
      toast.error('Please enter admin panel password');
      return;
    }
    
    setVerifyingLogin(true);
    try {
      await axios.post(
        `${API}/admin/verify-password`,
        { password: adminPanelPassword },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      // Store in session for subsequent API calls
      sessionStorage.setItem('adminToken', adminToken);
      sessionStorage.setItem('adminPanelPassword', adminPanelPassword);
      setIsAuthenticated(true);
      toast.success('Access granted');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('This account does not have admin access');
        setLoginStep(1);
        setAdminToken('');
      } else if (error.response?.status === 401) {
        toast.error('Invalid admin password');
      } else {
        toast.error('Authentication failed');
      }
    } finally {
      setVerifyingLogin(false);
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${adminToken}`,
    'X-Admin-Password': adminPanelPassword
  });

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminPanelPassword');
    setIsAuthenticated(false);
    setAdminToken('');
    setAdminPanelPassword('');
    setAdminEmail('');
    setAdminAccountPassword('');
    setLoginStep(1);
  };

  const fetchAdminData = async () => {
    if (!adminToken || !isAuthenticated) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, usersRes, couponsRes, stripeRes, ga4Res, aiRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users?limit=100`, { headers }),
        axios.get(`${API}/admin/coupons?limit=100`, { headers }),
        axios.get(`${API}/admin/settings/stripe`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings/ga4`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings/ai-features`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setCoupons(couponsRes.data.coupons || []);
      
      // Set Stripe settings
      if (stripeRes.data) {
        setStripeSettings(stripeRes.data);
        setStripePublishableKey(stripeRes.data.stripe_publishable_key || '');
      }
      
      // Set GA4 settings
      if (ga4Res.data) {
        setGa4Settings(ga4Res.data);
        setGa4MeasurementId(ga4Res.data.ga4_measurement_id || '');
      }
      
      // Set AI features
      if (aiRes.data) {
        setAiFeatures(aiRes.data);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        handleLogout();
      } else if (error.response?.status === 401) {
        // Password expired or invalid
        setIsAuthenticated(false);
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminPanelPassword');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateCoupons = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/generate-coupons?count=500`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to generate coupons');
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const exportCouponsCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/coupons/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'coupons_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Coupons exported successfully!');
    } catch (error) {
      toast.error('Failed to export coupons');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const saveStripeSettings = async () => {
    if (!stripeSecretKey && !stripeSettings?.configured) {
      toast.error('Please enter a Stripe Secret Key');
      return;
    }

    setSavingStripe(true);
    try {
      await axios.post(
        `${API}/admin/settings/stripe`,
        {
          stripe_secret_key: stripeSecretKey || stripeSettings?.stripe_secret_key,
          stripe_publishable_key: stripePublishableKey
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Stripe settings saved successfully!');
      setStripeSecretKey(''); // Clear the input for security
      fetchAdminData(); // Refresh to get updated masked key
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save Stripe settings';
      toast.error(errorMsg);
    } finally {
      setSavingStripe(false);
    }
  };

  const deleteStripeSettings = async () => {
    if (!window.confirm('Are you sure you want to remove Stripe configuration? The system will fall back to environment variables if available.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/settings/stripe`,
        { headers: getAuthHeaders() }
      );
      toast.success('Stripe settings removed');
      setStripeSecretKey('');
      setStripePublishableKey('');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to remove Stripe settings');
    }
  };

  const saveGa4Settings = async () => {
    if (!ga4MeasurementId.trim()) {
      toast.error('Please enter a GA4 Measurement ID');
      return;
    }
    
    if (!ga4MeasurementId.startsWith('G-')) {
      toast.error('GA4 Measurement ID must start with "G-"');
      return;
    }

    setSavingGa4(true);
    try {
      await axios.post(
        `${API}/admin/settings/ga4`,
        { ga4_measurement_id: ga4MeasurementId },
        { headers: getAuthHeaders() }
      );
      toast.success('GA4 tracking enabled! Reload the page to activate.');
      fetchAdminData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save GA4 settings';
      toast.error(errorMsg);
    } finally {
      setSavingGa4(false);
    }
  };

  const deleteGa4Settings = async () => {
    if (!window.confirm('Are you sure you want to disable GA4 tracking?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/settings/ga4`,
        { headers: getAuthHeaders() }
      );
      toast.success('GA4 tracking disabled');
      setGa4MeasurementId('');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to disable GA4 tracking');
    }
  };

  // AI Features toggle
  const toggleAiFeature = async (feature, enabled) => {
    setSavingAiFeatures(true);
    try {
      const params = new URLSearchParams();
      if (feature === 'post_editing') {
        params.append('post_editing_enabled', enabled);
      } else if (feature === 'ai_optimization') {
        params.append('ai_optimization_enabled', enabled);
      }
      
      await axios.post(
        `${API}/admin/settings/ai-features?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(`${feature === 'post_editing' ? 'Post Editing' : 'AI Optimization'} ${enabled ? 'enabled' : 'disabled'}`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update AI feature settings');
    } finally {
      setSavingAiFeatures(false);
    }
  };

  // Plan Management Functions
  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/admin/plans`, { headers: getAuthHeaders() });
      const allPlans = response.data.plans || [];
      setPlans({
        monthly: allPlans.filter(p => p.plan_type === 'monthly'),
        lifetime: allPlans.filter(p => p.plan_type === 'lifetime')
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const initializeDefaultPlans = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/plans/initialize-defaults`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.initialized) {
        toast.success(response.data.message);
        if (response.data.migrated_coupons > 0) {
          toast.info(`Migrated ${response.data.migrated_coupons} existing coupons to Lifetime Business plan`);
        }
        fetchPlans();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to initialize plans');
    }
  };

  const openPlanModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanFormData({
        name: plan.name,
        plan_type: plan.plan_type,
        price: plan.price,
        stripe_price_id: plan.stripe_price_id || '',
        display_order: plan.display_order || 0,
        is_active: plan.is_active,
        features: { ...plan.features }
      });
    } else {
      setEditingPlan(null);
      setPlanFormData({
        name: '',
        plan_type: 'monthly',
        price: 0,
        stripe_price_id: '',
        display_order: 0,
        is_active: true,
        features: {
          max_sites: 1,
          max_posts_analyzed: 300,
          max_seats: 1,
          bulk_delete_limit: 50,
          has_ga4_connection: true,
          has_sorting_filtering: true,
          dashboard_days: -1
        }
      });
    }
    setShowPlanModal(true);
  };

  const closePlanModal = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
  };

  const savePlan = async () => {
    if (!planFormData.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    
    setSavingPlan(true);
    try {
      if (editingPlan) {
        await axios.put(
          `${API}/admin/plans/${editingPlan.id}`,
          planFormData,
          { headers: getAuthHeaders() }
        );
        toast.success('Plan updated successfully');
      } else {
        await axios.post(
          `${API}/admin/plans`,
          planFormData,
          { headers: getAuthHeaders() }
        );
        toast.success('Plan created successfully');
      }
      closePlanModal();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await axios.delete(`${API}/admin/plans/${planId}`, { headers: getAuthHeaders() });
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const openCouponModal = (plan) => {
    setSelectedPlanForCoupons(plan);
    setCouponCount(100);
    setShowCouponModal(true);
  };

  const generatePlanCoupons = async () => {
    if (!selectedPlanForCoupons) return;
    
    setGeneratingPlanCoupons(true);
    try {
      const response = await axios.post(
        `${API}/admin/plans/${selectedPlanForCoupons.id}/generate-coupons`,
        { count: couponCount },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setShowCouponModal(false);
      fetchPlans();
      fetchAdminData(); // Refresh coupon stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate coupons');
    } finally {
      setGeneratingPlanCoupons(false);
    }
  };

  // Load plans when authenticated
  useEffect(() => {
    if (isAuthenticated && adminToken) {
      fetchPlans();
    }
  }, [isAuthenticated, adminToken]);

  const getPlanBadge = (plan) => {
    const badges = {
      free: 'bg-slate-100 text-slate-700',
      pro: 'bg-purple-100 text-purple-700',
      lifetime: 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700'
    };
    return badges[plan] || badges.free;
  };

  // Admin login gate - completely independent from user auth
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 border-white/20 text-white">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-4 bg-purple-600/20 rounded-full w-fit">
              <Shield className="w-8 h-8 text-purple-400" />
            </div>
            <CardTitle className="text-2xl">Admin Panel</CardTitle>
            <p className="text-slate-400 mt-2">
              {loginStep === 1 ? 'Login with admin credentials' : 'Enter admin panel password'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {loginStep === 1 ? (
              <>
                {/* Step 1: Email and Account Password */}
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Admin Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="admin@example.com"
                      className="bg-white/5 border-white/20 text-white pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Account Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type={showAccountPassword ? 'text' : 'password'}
                      value={adminAccountPassword}
                      onChange={(e) => setAdminAccountPassword(e.target.value)}
                      placeholder="Your account password"
                      className="bg-white/5 border-white/20 text-white pl-10 pr-10"
                      onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccountPassword(!showAccountPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showAccountPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  onClick={handleAdminLogin}
                  disabled={verifyingLogin}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {verifyingLogin ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                  )}
                  Continue
                </Button>
              </>
            ) : (
              <>
                {/* Step 2: Admin Panel Password */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-2">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Logged in as {adminEmail}
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Admin Panel Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type={showPanelPassword ? 'text' : 'password'}
                      value={adminPanelPassword}
                      onChange={(e) => setAdminPanelPassword(e.target.value)}
                      placeholder="Admin panel password"
                      className="bg-white/5 border-white/20 text-white pl-10 pr-10"
                      onKeyDown={(e) => e.key === 'Enter' && verifyAdminPanelPassword()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPanelPassword(!showPanelPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPanelPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  onClick={verifyAdminPanelPassword}
                  disabled={verifyingLogin}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {verifyingLogin ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Access Admin Panel
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => { setLoginStep(1); setAdminToken(''); }}
                  className="w-full text-slate-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Use different account
                </Button>
              </>
            )}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="w-full text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Home
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-400" />
              <h1 className="text-2xl font-bold">Admin Panel</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchAdminData} variant="outline" className="border-white/20 text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout} 
              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
              <Lock className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/10 border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300">Total Users</p>
                  <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
                </div>
                <Users className="w-10 h-10 text-cyan-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300">New (7 days)</p>
                  <p className="text-3xl font-bold">{stats?.new_users?.last_7_days || 0}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300">Total Revenue</p>
                  <p className="text-3xl font-bold">${stats?.revenue?.total || 0}</p>
                </div>
                <DollarSign className="w-10 h-10 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300">Coupons Left</p>
                  <p className="text-3xl font-bold">{stats?.coupons?.available || 0}</p>
                </div>
                <Ticket className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan Distribution */}
        <Card className="bg-white/10 border-white/20 text-white mb-8">
          <CardHeader>
            <CardTitle>Users by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-500/20 rounded-lg">
                <p className="text-3xl font-bold">{stats?.users_by_plan?.free || 0}</p>
                <p className="text-slate-300">Free</p>
              </div>
              <div className="text-center p-4 bg-purple-500/20 rounded-lg">
                <p className="text-3xl font-bold">{stats?.users_by_plan?.pro || 0}</p>
                <p className="text-purple-300">Pro</p>
              </div>
              <div className="text-center p-4 bg-amber-500/20 rounded-lg">
                <p className="text-3xl font-bold">{stats?.users_by_plan?.lifetime || 0}</p>
                <p className="text-amber-300">Lifetime</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button 
            variant={activeTab === 'users' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-purple-600' : 'text-white'}
          >
            <Users className="w-4 h-4 mr-2" /> Users
          </Button>
          <Button 
            variant={activeTab === 'plans' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('plans')}
            className={activeTab === 'plans' ? 'bg-purple-600' : 'text-white'}
          >
            <Package className="w-4 h-4 mr-2" /> Plans
          </Button>
          <Button 
            variant={activeTab === 'coupons' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('coupons')}
            className={activeTab === 'coupons' ? 'bg-purple-600' : 'text-white'}
          >
            <Ticket className="w-4 h-4 mr-2" /> Coupons
          </Button>
          <Button 
            variant={activeTab === 'stripe' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('stripe')}
            className={activeTab === 'stripe' ? 'bg-purple-600' : 'text-white'}
          >
            <CreditCard className="w-4 h-4 mr-2" /> Stripe Settings
          </Button>
          <Button 
            variant={activeTab === 'ga4' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('ga4')}
            className={activeTab === 'ga4' ? 'bg-purple-600' : 'text-white'}
          >
            <BarChart3 className="w-4 h-4 mr-2" /> GA4 Tracking
          </Button>
          <Button 
            variant={activeTab === 'ai' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('ai')}
            className={activeTab === 'ai' ? 'bg-purple-600' : 'text-white'}
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Features
          </Button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="bg-white/10 border-white/20 text-white">
            <CardHeader>
              <CardTitle>All Users ({users.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Plan</th>
                      <th className="text-left py-3 px-4">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, idx) => (
                      <tr key={idx} className="border-b border-white/10 hover:bg-white/5">
                        <td className="py-3 px-4">{user.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPlanBadge(user.subscription?.plan)}`}>
                            {user.subscription?.plan?.toUpperCase() || 'FREE'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-400">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            {/* Initialize Plans Button */}
            {plans.monthly.length === 0 && plans.lifetime.length === 0 && (
              <Card className="bg-amber-500/10 border-amber-500/30 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-amber-400">No Plans Configured</h3>
                      <p className="text-sm text-slate-400 mt-1">Initialize default plans to get started</p>
                    </div>
                    <Button onClick={initializeDefaultPlans} className="bg-amber-600 hover:bg-amber-700">
                      <Zap className="w-4 h-4 mr-2" /> Initialize Default Plans
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Plans */}
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Monthly Plans ({plans.monthly.length})
                </CardTitle>
                <Button onClick={() => { setPlanFormData(prev => ({ ...prev, plan_type: 'monthly' })); openPlanModal(); }} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Monthly Plan
                </Button>
              </CardHeader>
              <CardContent>
                {plans.monthly.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No monthly plans configured</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {plans.monthly.map((plan) => (
                      <div key={plan.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-lg">{plan.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs ${plan.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-blue-400 mb-3">${plan.price}<span className="text-sm text-slate-400">/month</span></p>
                        <div className="space-y-1 text-sm text-slate-300 mb-4">
                          <p>{plan.features.max_sites} WordPress site(s)</p>
                          <p>{plan.features.max_posts_analyzed} posts analyzed</p>
                          <p>{plan.features.bulk_delete_limit} bulk deletes/month</p>
                          <p>{plan.features.max_seats} seat(s)</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openPlanModal(plan)} className="flex-1 border-white/20 text-white">
                            <Edit2 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deletePlan(plan.id)} className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lifetime Plans */}
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Lifetime Deal Plans ({plans.lifetime.length})
                </CardTitle>
                <Button onClick={() => { setPlanFormData(prev => ({ ...prev, plan_type: 'lifetime' })); openPlanModal(); }} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Lifetime Plan
                </Button>
              </CardHeader>
              <CardContent>
                {plans.lifetime.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No lifetime plans configured</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {plans.lifetime.map((plan) => (
                      <div key={plan.id} className="bg-white/5 border border-amber-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-lg">{plan.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs ${plan.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-amber-400 mb-2">${plan.price}<span className="text-sm text-slate-400"> one-time</span></p>
                        {plan.coupon_stats && (
                          <div className="bg-purple-500/10 rounded px-2 py-1 mb-3 text-xs">
                            <span className="text-purple-400">{plan.coupon_stats.available} coupons available</span>
                            <span className="text-slate-500 ml-2">({plan.coupon_stats.used} used)</span>
                          </div>
                        )}
                        <div className="space-y-1 text-sm text-slate-300 mb-4">
                          <p>{plan.features.max_sites} WordPress site(s)</p>
                          <p>{plan.features.max_posts_analyzed} posts analyzed</p>
                          <p>{plan.features.bulk_delete_limit} bulk deletes/month</p>
                          <p>{plan.features.max_seats} seat(s)</p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => openPlanModal(plan)} className="flex-1 border-white/20 text-white">
                            <Edit2 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" onClick={() => openCouponModal(plan)} className="flex-1 bg-purple-600 hover:bg-purple-700">
                            <Ticket className="w-3 h-3 mr-1" /> Coupons
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deletePlan(plan.id)} className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <Card className="bg-white/10 border-white/20 text-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Coupon Codes ({coupons.length})</CardTitle>
              <div className="flex gap-2">
                <Button 
                  onClick={exportCouponsCSV} 
                  disabled={exporting || coupons.length === 0} 
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                >
                  {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
                <Button onClick={generateCoupons} disabled={generating} className="bg-purple-600 hover:bg-purple-700">
                  {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
                  Generate 500 Coupons
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4">Code</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Used By</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon, idx) => (
                      <tr key={idx} className="border-b border-white/10 hover:bg-white/5">
                        <td className="py-3 px-4 font-mono">{coupon.code}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${coupon.used ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                            {coupon.used ? 'Used' : 'Available'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-400">{coupon.used_by || '-'}</td>
                        <td className="py-3 px-4">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => copyCode(coupon.code)}
                            className="text-white"
                          >
                            {copiedCode === coupon.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stripe Settings Tab */}
        {activeTab === 'stripe' && (
          <Card className="bg-white/10 border-white/20 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Stripe Payment Configuration
                </CardTitle>
                {stripeSettings?.configured && (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Configured</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Banner */}
              {stripeSettings?.configured ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-green-400 font-medium">Stripe is configured and active</p>
                      <p className="text-sm text-slate-400 mt-1">
                        {stripeSettings.source === 'environment' 
                          ? 'Using environment variable configuration'
                          : `Last updated: ${stripeSettings.updated_at ? new Date(stripeSettings.updated_at).toLocaleDateString() : 'N/A'}`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium">Stripe is not configured</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Payments are disabled. Enter your Stripe API keys below to enable checkout.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Key Display */}
              {stripeSettings?.configured && stripeSettings?.stripe_secret_key_masked && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-2">Current Secret Key</p>
                  <p className="font-mono text-slate-300">{stripeSettings.stripe_secret_key_masked}</p>
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Stripe Secret Key {stripeSettings?.configured && <span className="text-slate-500">(leave blank to keep current)</span>}
                  </label>
                  <div className="relative">
                    <Input
                      type={showSecretKey ? 'text' : 'password'}
                      value={stripeSecretKey}
                      onChange={(e) => setStripeSecretKey(e.target.value)}
                      placeholder={stripeSettings?.configured ? 'Enter new key to update...' : 'sk_live_... or sk_test_...'}
                      className="bg-white/5 border-white/20 text-white pr-10 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Found in Stripe Dashboard → Developers → API keys
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Stripe Publishable Key
                  </label>
                  <Input
                    type="text"
                    value={stripePublishableKey}
                    onChange={(e) => setStripePublishableKey(e.target.value)}
                    placeholder="pk_live_... or pk_test_..."
                    className="bg-white/5 border-white/20 text-white font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Optional - used for frontend Stripe.js integration if needed
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-400 font-medium mb-2">💡 How to get your Stripe API keys:</p>
                <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Stripe Dashboard → API Keys</a></li>
                  <li>Copy your <strong>Secret key</strong> (starts with sk_live_ or sk_test_)</li>
                  <li>Optionally copy your <strong>Publishable key</strong> (starts with pk_live_ or pk_test_)</li>
                  <li>Use test keys (sk_test_) for testing, live keys (sk_live_) for production</li>
                </ol>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={saveStripeSettings}
                  disabled={savingStripe || (!stripeSecretKey && !stripeSettings?.configured)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingStripe ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Stripe Settings
                </Button>
                
                {stripeSettings?.configured && stripeSettings?.source !== 'environment' && (
                  <Button 
                    onClick={deleteStripeSettings}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Configuration
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* GA4 Tracking Tab */}
        {activeTab === 'ga4' && (
          <Card className="bg-white/10 border-white/20 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Google Analytics 4 Tracking
                </CardTitle>
                {ga4Settings?.configured && (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Active</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Banner */}
              {ga4Settings?.configured ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-green-400 font-medium">GA4 tracking is active</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Measurement ID: <span className="font-mono">{ga4Settings.ga4_measurement_id}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium">GA4 tracking is not configured</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Add your GA4 Measurement ID to track visitor analytics for WP Magica.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Field */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  GA4 Measurement ID
                </label>
                <Input
                  type="text"
                  value={ga4MeasurementId}
                  onChange={(e) => setGa4MeasurementId(e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  className="bg-white/5 border-white/20 text-white font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Found in GA4 Admin → Data Streams → Your Web Stream
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-400 font-medium mb-2">💡 How to get your GA4 Measurement ID:</p>
                <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Analytics</a></li>
                  <li>Select or create a GA4 property</li>
                  <li>Go to Admin → Data Streams → Web</li>
                  <li>Copy the Measurement ID (starts with G-)</li>
                </ol>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={saveGa4Settings}
                  disabled={savingGa4 || !ga4MeasurementId.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingGa4 ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save GA4 Settings
                </Button>
                
                {ga4Settings?.configured && (
                  <Button 
                    onClick={deleteGa4Settings}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Disable Tracking
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Features Tab */}
        {activeTab === 'ai' && (
          <Card className="bg-white/10 border-white/20 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  AI Features Control
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-400">
                  Control which AI features are available to users. These features require users to configure their own API keys (Gemini for text, Airbrush for images).
                </p>
              </div>

              {/* Post Editing Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <h3 className="font-medium">Post Editing</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Allow users to edit WordPress posts directly from WP Magica
                  </p>
                </div>
                <button
                  onClick={() => toggleAiFeature('post_editing', !aiFeatures.post_editing_enabled)}
                  disabled={savingAiFeatures}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    aiFeatures.post_editing_enabled ? 'bg-green-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      aiFeatures.post_editing_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* AI Optimization Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <h3 className="font-medium">AI Optimization</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Enable AI-powered title generation, SEO analysis, and image generation
                  </p>
                </div>
                <button
                  onClick={() => toggleAiFeature('ai_optimization', !aiFeatures.ai_optimization_enabled)}
                  disabled={savingAiFeatures}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    aiFeatures.ai_optimization_enabled ? 'bg-green-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      aiFeatures.ai_optimization_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Status Summary */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div className={`p-3 rounded-lg ${aiFeatures.post_editing_enabled ? 'bg-green-500/10' : 'bg-slate-500/10'}`}>
                  <p className={`text-sm font-medium ${aiFeatures.post_editing_enabled ? 'text-green-400' : 'text-slate-400'}`}>
                    Post Editing
                  </p>
                  <p className={`text-xs ${aiFeatures.post_editing_enabled ? 'text-green-300' : 'text-slate-500'}`}>
                    {aiFeatures.post_editing_enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${aiFeatures.ai_optimization_enabled ? 'bg-green-500/10' : 'bg-slate-500/10'}`}>
                  <p className={`text-sm font-medium ${aiFeatures.ai_optimization_enabled ? 'text-green-400' : 'text-slate-400'}`}>
                    AI Optimization
                  </p>
                  <p className={`text-xs ${aiFeatures.ai_optimization_enabled ? 'text-green-300' : 'text-slate-500'}`}>
                    {aiFeatures.ai_optimization_enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>

              {/* Features Info */}
              <div className="space-y-3 pt-4 border-t border-white/10">
                <h4 className="text-sm font-medium text-slate-300">What users get with AI features:</h4>
                <ul className="text-sm text-slate-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400 mt-0.5" />
                    <span><strong>AI Titles:</strong> Generate SEO-optimized title suggestions using Google Gemini</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400 mt-0.5" />
                    <span><strong>SEO Analysis:</strong> Get content recommendations and scoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400 mt-0.5" />
                    <span><strong>AI Images:</strong> Generate and insert images using Airbrush.ai</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400 mt-0.5" />
                    <span><strong>Post Editor:</strong> Edit posts directly with before/after preview</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plan Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg bg-slate-900 border-white/20 text-white max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={closePlanModal}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Plan Name</label>
                  <Input
                    value={planFormData.name}
                    onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
                    placeholder="e.g., Starter, Pro, Business"
                    className="bg-white/5 border-white/20 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Plan Type</label>
                    <select
                      value={planFormData.plan_type}
                      onChange={(e) => setPlanFormData({ ...planFormData, plan_type: e.target.value })}
                      className="w-full bg-white/5 border border-white/20 text-white rounded-md px-3 py-2"
                      disabled={!!editingPlan}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Price ($)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={planFormData.price}
                      onChange={(e) => setPlanFormData({ ...planFormData, price: parseFloat(e.target.value) || 0 })}
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>
                </div>

                {planFormData.plan_type === 'monthly' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Stripe Price ID</label>
                    <Input
                      value={planFormData.stripe_price_id}
                      onChange={(e) => setPlanFormData({ ...planFormData, stripe_price_id: e.target.value })}
                      placeholder="price_..."
                      className="bg-white/5 border-white/20 text-white font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">From Stripe Products → Pricing</p>
                  </div>
                )}

                <div className="border-t border-white/10 pt-4">
                  <h4 className="font-medium mb-3">Features</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">WordPress Sites</label>
                      <Input
                        type="number"
                        min="1"
                        value={planFormData.features.max_sites}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, max_sites: parseInt(e.target.value) || 1 }
                        })}
                        className="bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Posts Analyzed</label>
                      <Input
                        type="number"
                        min="1"
                        value={planFormData.features.max_posts_analyzed}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, max_posts_analyzed: parseInt(e.target.value) || 100 }
                        })}
                        className="bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Seats</label>
                      <Input
                        type="number"
                        min="1"
                        value={planFormData.features.max_seats}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, max_seats: parseInt(e.target.value) || 1 }
                        })}
                        className="bg-white/5 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Bulk Delete/Month</label>
                      <Input
                        type="number"
                        min="1"
                        value={planFormData.features.bulk_delete_limit}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, bulk_delete_limit: parseInt(e.target.value) || 50 }
                        })}
                        className="bg-white/5 border-white/20 text-white"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={planFormData.features.has_ga4_connection}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, has_ga4_connection: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm text-slate-300">GA4 Connection</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={planFormData.features.has_sorting_filtering}
                        onChange={(e) => setPlanFormData({
                          ...planFormData,
                          features: { ...planFormData.features, has_sorting_filtering: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm text-slate-300">Sorting/Filtering</span>
                    </label>
                  </div>

                  <label className="flex items-center gap-2 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={planFormData.is_active}
                      onChange={(e) => setPlanFormData({ ...planFormData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm text-slate-300">Plan is Active</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={savePlan} disabled={savingPlan} className="flex-1 bg-purple-600 hover:bg-purple-700">
                    {savingPlan ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                  <Button variant="outline" onClick={closePlanModal} className="border-white/20 text-white">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Generate Coupons Modal */}
        {showCouponModal && selectedPlanForCoupons && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md bg-slate-900 border-white/20 text-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Generate Coupons</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCouponModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-amber-400 font-medium">{selectedPlanForCoupons.name}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {selectedPlanForCoupons.features.max_sites} sites, {selectedPlanForCoupons.features.max_posts_analyzed} posts, {selectedPlanForCoupons.features.bulk_delete_limit} bulk deletes
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Number of Coupons</label>
                  <Input
                    type="number"
                    min="1"
                    max="5000"
                    value={couponCount}
                    onChange={(e) => setCouponCount(Math.min(5000, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="bg-white/5 border-white/20 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Max 5000 coupons per batch</p>
                </div>

                {selectedPlanForCoupons.coupon_stats && (
                  <div className="text-sm text-slate-400">
                    Current: {selectedPlanForCoupons.coupon_stats.available} available, {selectedPlanForCoupons.coupon_stats.used} used
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={generatePlanCoupons} 
                    disabled={generatingPlanCoupons} 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingPlanCoupons ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Ticket className="w-4 h-4 mr-2" />
                    )}
                    Generate {couponCount} Coupons
                  </Button>
                  <Button variant="outline" onClick={() => setShowCouponModal(false)} className="border-white/20 text-white">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
